import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appeler, cleNeuve } from '../../api/client.ts';
import { useSession } from '../../composants/session.tsx';
import { Carte, Chargement, DateHeure, Erreur, Etiquette, Nombre } from '../../composants/communs.tsx';
import { MOTIF_ECART_MIN, type DetailTransfert } from './types.ts';

/**
 * Détail d'un transfert, et sa réception — RG-053.
 *
 * Les quantités sont pré-remplies avec ce qui a été expédié : le cas normal
 * se confirme d'un clic. Dès qu'un chiffre diffère, un motif devient
 * obligatoire — c'est ce motif qui, plus tard, expliquera un écart de stock
 * que personne ne saurait plus justifier.
 */
export function ReceptionTransfert({
  transfertId,
  onFerme,
}: {
  transfertId: string;
  onFerme: () => void;
}): ReactNode {
  const { peut } = useSession();
  const client = useQueryClient();
  const [recues, setRecues] = useState<Record<string, string>>({});
  const [motif, setMotif] = useState('');
  const [cle] = useState(() => cleNeuve());

  const { data, isPending, error } = useQuery({
    queryKey: ['transfert', transfertId],
    queryFn: () => appeler<DetailTransfert>(`/transferts/${transfertId}`),
  });

  // Pré-remplissage : le cas normal est « tout est arrivé ».
  useEffect(() => {
    if (data !== undefined && data.statut === 'EXPEDIE') {
      setRecues(
        Object.fromEntries(
          data.lignes.map((ligne) => [ligne.produit_id, String(ligne.quantite_expediee)]),
        ),
      );
    }
  }, [data]);

  const recevable = data?.a_recevoir === true && peut('transfert.receptionner');
  const lignes = data?.lignes ?? [];
  const ecart = lignes.some(
    (ligne) => Number(recues[ligne.produit_id] ?? '0') !== ligne.quantite_expediee,
  );
  const saisieValide = lignes.every((ligne) => /^\d+$/.test(recues[ligne.produit_id] ?? ''));
  const motifSuffisant = !ecart || motif.trim().length >= MOTIF_ECART_MIN;

  const reception = useMutation({
    mutationFn: () =>
      appeler(`/transferts/${transfertId}/reception`, {
        methode: 'POST',
        cleIdempotence: cle,
        corps: {
          lignes: lignes.map((ligne) => ({
            produit_id: ligne.produit_id,
            quantite_recue: Number(recues[ligne.produit_id] ?? '0'),
          })),
          motif: motif.trim() === '' ? null : motif.trim(),
        },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['transferts'] });
      void client.invalidateQueries({ queryKey: ['transfert', transfertId] });
      void client.invalidateQueries({ queryKey: ['produits'] });
      onFerme();
    },
  });

  return (
    <Carte
      titre={`Transfert ${data?.numero ?? ''}`}
      actions={
        <button type="button" className="bouton--discret" onClick={onFerme}>
          Fermer
        </button>
      }
    >
      <Erreur erreur={error} />
      {isPending ? (
        <Chargement />
      ) : data === undefined ? null : (
        <>
          <p>
            <span className="mono">
              {data.source} → {data.destination}
            </span>{' '}
            · envoyé par {data.expedie_par}, <DateHeure valeur={data.expedie_le} />
            {data.recu_le !== null && (
              <>
                {' '}· reçu par {data.recu_par}, <DateHeure valeur={data.recu_le} />
              </>
            )}
            {data.motif !== null && <> · {data.motif}</>}
          </p>

          <div className="tableau-defilant">
            <table>
              <thead>
                <tr>
                  <th>Produit</th>
                  <th>Expédié</th>
                  <th>{recevable ? 'Reçu' : 'Reçu'}</th>
                  <th>Écart</th>
                </tr>
              </thead>
              <tbody>
                {lignes.map((ligne) => {
                  const saisie = recues[ligne.produit_id] ?? '';
                  const difference = recevable
                    ? Number(saisie === '' ? '0' : saisie) - ligne.quantite_expediee
                    : ligne.ecart;

                  return (
                    <tr key={ligne.produit_id}>
                      <td>
                        {ligne.designation} <span className="mono">{ligne.reference}</span>
                      </td>
                      <td>
                        <Nombre valeur={ligne.quantite_expediee} />
                      </td>
                      <td>
                        {recevable ? (
                          <input
                            aria-label={`Quantité reçue — ${ligne.designation}`}
                            inputMode="numeric"
                            value={saisie}
                            onChange={(evenement) => {
                              setRecues({
                                ...recues,
                                [ligne.produit_id]: evenement.target.value.replace(/\D/g, ''),
                              });
                            }}
                          />
                        ) : ligne.quantite_recue === null ? (
                          '—'
                        ) : (
                          <Nombre valeur={ligne.quantite_recue} />
                        )}
                      </td>
                      <td>
                        {difference === null || difference === 0 ? (
                          <Nombre valeur={0} />
                        ) : (
                          <Etiquette
                            ton={difference < 0 ? 'rouge' : 'ambre'}
                            texte={difference > 0 ? `+${String(difference)}` : String(difference)}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {recevable && (
            <>
              {ecart && (
                <div className="champ" style={{ marginTop: 16 }}>
                  <label htmlFor="motif-ecart">
                    Écart constaté — expliquez en {MOTIF_ECART_MIN} caractères au moins
                  </label>
                  <textarea
                    id="motif-ecart"
                    rows={2}
                    value={motif}
                    onChange={(evenement) => {
                      setMotif(evenement.target.value);
                    }}
                  />
                </div>
              )}

              <Erreur erreur={reception.error} />
              <div className="ligne" style={{ marginTop: 12 }}>
                <button
                  type="button"
                  disabled={!saisieValide || !motifSuffisant || reception.isPending}
                  onClick={() => {
                    reception.mutate();
                  }}
                >
                  {reception.isPending ? 'Réception…' : 'Confirmer la réception'}
                </button>
              </div>
            </>
          )}
        </>
      )}
    </Carte>
  );
}
